import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Outlet, NavLink, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../stores/auth'
import { api } from '../../lib/api'
import { clearPushToken } from '../../lib/fcm'
import { DemoBanner } from '../DemoBanner'
import { useModule, type ModuleKey } from '../../lib/modules'

const navItems: { to: string; label: string; icon: string; end?: boolean; module?: ModuleKey }[] = [
  { to: '/owner', label: 'Обзор', icon: '📊', end: true },
  { to: '/owner/attention', label: 'Требует внимания', icon: '⚠️' },
  { to: '/owner/shifts', label: 'Смены', icon: '🕐' },
  { to: '/owner/rentals', label: 'Аренды', icon: '🚲' },
  { to: '/owner/reports', label: 'Отчёты', icon: '📈' },
  { to: '/owner/journal', label: 'Журнал', icon: '📋' },
  { to: '/owner/payments', label: 'Платежи', icon: '💳' },
  { to: '/owner/bikes', label: 'Велосипеды', icon: '🔑' },
  { to: '/owner/warehouse', label: 'Склад', icon: '📦', module: 'service' },
  { to: '/owner/service-report', label: 'Сервис', icon: '🔧', module: 'service' },
  { to: '/owner/points', label: 'Точки', icon: '📍' },
  { to: '/owner/tariffs', label: 'Тарифы', icon: '💰' },
  { to: '/owner/managers', label: 'Менеджеры', icon: '👤' },
  { to: '/owner/audit', label: 'Аудит', icon: '🔍' },
  { to: '/owner/settings', label: 'Настройки', icon: '⚙️' },
]

export default function OwnerLayout() {
  const { user, logout } = useAuthStore()
  const navigate = useNavigate()
  const [menuOpen, setMenuOpen] = useState(false)

  const { data: stats } = useQuery<{ requiresAttentionCount?: number }>({
    queryKey: ['stats'],
    queryFn: () => api.get('/rentals/stats').then((r) => r.data),
    staleTime: 45_000,
    refetchInterval: 2 * 60_000,
  })
  const attentionCount = stats?.requiresAttentionCount ?? 0

  const serviceEnabled = useModule('service')
  const visibleNavItems = navItems.filter((item) => !item.module || (item.module === 'service' && serviceEnabled))

  const handleLogout = async () => {
    const deadline = new Promise<void>((resolve) => setTimeout(resolve, 2000))
    await Promise.race([clearPushToken().catch(() => {}), deadline])
    try { await Promise.race([api.post('/auth/logout'), deadline]) } catch {}
    logout()
    navigate('/login')
  }

  const NavItems = ({ onClick }: { onClick?: () => void }) => (
    <>
      {visibleNavItems.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          onClick={onClick}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
              isActive
                ? 'bg-brand-50 text-brand-700 font-medium'
                : 'text-gray-600 hover:bg-gray-100'
            }`
          }
        >
          <span className="text-base">{item.icon}</span>
          <span className="flex-1 min-w-0">{item.label}</span>
          {item.to === '/owner/attention' && attentionCount > 0 && (
            <span
              className="shrink-0 min-w-[1.25rem] h-5 px-1.5 flex items-center justify-center rounded-full bg-orange-500 text-white text-xs font-bold tabular-nums"
              title={`${attentionCount} аренд требуют внимания`}
            >
              {attentionCount > 99 ? '99+' : attentionCount}
            </span>
          )}
        </NavLink>
      ))}
    </>
  )

  return (
    <div className="min-h-screen flex bg-gray-50">

      {/* ── Desktop sidebar (hidden on mobile) ──────────────── */}
      <aside className="hidden md:flex w-56 bg-white border-r border-gray-200 flex-col shrink-0">
        <div className="p-4 border-b border-gray-200">
          <h1 className="text-xl font-bold text-brand-600">🚲 VeloRent</h1>
          <p className="text-xs text-gray-500 mt-1 truncate">{user?.name}</p>
        </div>
        <nav className="flex-1 p-2 space-y-0.5 overflow-y-auto">
          <NavItems />
        </nav>
        <div className="p-3 border-t border-gray-200">
          <button
            onClick={handleLogout}
            className="w-full text-left text-sm text-gray-500 hover:text-red-600 px-3 py-2 rounded-lg hover:bg-red-50 transition-colors"
          >
            Выйти
          </button>
        </div>
      </aside>

      {/* ── Mobile layout ───────────────────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0">

        {/* Mobile header */}
        <header className="md:hidden bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between sticky top-0 z-30">
          <div>
            <span className="font-bold text-brand-600">🚲 VeloRent</span>
            <span className="text-xs text-gray-400 ml-2">{user?.name}</span>
          </div>
          <button
            onClick={() => setMenuOpen(true)}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-600"
            aria-label="Меню"
          >
            ☰
          </button>
        </header>

        {/* Mobile slide-in drawer */}
        {menuOpen && (
          <div className="md:hidden fixed inset-0 z-50 flex">
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-black/40"
              onClick={() => setMenuOpen(false)}
            />
            {/* Drawer */}
            <div className="relative w-64 bg-white flex flex-col shadow-xl">
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <h1 className="font-bold text-brand-600">🚲 VeloRent</h1>
                  <p className="text-xs text-gray-500 mt-0.5">{user?.name}</p>
                </div>
                <button
                  onClick={() => setMenuOpen(false)}
                  className="text-gray-400 text-xl p-1"
                >
                  ✕
                </button>
              </div>
              <nav className="flex-1 p-2 space-y-0.5 overflow-y-auto">
                <NavItems onClick={() => setMenuOpen(false)} />
              </nav>
              <div className="p-3 border-t border-gray-200">
                <button
                  onClick={handleLogout}
                  className="w-full text-left text-sm text-gray-500 hover:text-red-600 px-3 py-2 rounded-lg hover:bg-red-50 transition-colors"
                >
                  Выйти
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Demo tenant warning banner */}
        <DemoBanner />

        {/* Page content */}
        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
